import type { KitTextLocale } from 'fym-shared/src/music24/kitText';
import type { ReleaseKitLook } from 'fym-shared/src/music24/releaseThemes';
import type { KitShape, StudioMoment } from 'fym-shared/src/music24/studioThemes';
import type { StoryFormatId } from 'fym-shared/src/music24/story/storyFormats';
import type { KitStory } from 'fym-shared/src/music24/story/storyTypes';
import { studioStory } from '../../frames/studioStory';
import { countdownDays } from '../../studioRules';

/** The song and theme facts that every Story preview of step 3 draws with. */
export interface StoryPreviewContext {
  look: ReleaseKitLook;
  palette: readonly string[];
  artworkUrl: string | undefined;
  /** The kit logo; null with no logo. */
  logoUrl: string | null;
  title: string;
  artist: string;
  /** `YYYY-MM-DD`; null with no date. */
  releaseDate: string | null;
  daysToRelease: number | null;
  lyrics: string | null;
  /** The SmartLink address under the pill; null: none. */
  link: string | null;
  /** The song has a published pre-save page; false: a Story and a feed post say "Coming soon". */
  presave: boolean;
  /** Free plans: the watermark of the file. */
  watermark: boolean;
  /** Today as `YYYY-MM-DD` (UTC). */
  today: string;
  /** The language of the kit words, as the render writes them; absent: English. */
  locale?: KitTextLocale;
}

/**
 * The image asset types drawn with the Story stack: the Story, the feed post (design e) and the
 * lyric card (the lyric Story fitted to a square, design k).
 */
export type StoryStackAssetType = 'story' | 'feedPost' | 'lyricCard';

/** True for an asset type that the Studio draws with the Story stack. */
export function isStoryStackType(assetTypeId: string): assetTypeId is StoryStackAssetType {
  return assetTypeId === 'story' || assetTypeId === 'feedPost' || assetTypeId === 'lyricCard';
}

/** The frame of the stack: a Story, the feed post of the picked shape (4:5 unless 1:1), or the lyric card. */
export function storyFormatOfStudio(assetTypeId: StoryStackAssetType, shape?: KitShape): StoryFormatId {
  if (assetTypeId === 'story') {
    return 'story';
  }
  if (assetTypeId === 'lyricCard') {
    return 'lyric11';
  }
  return shape === '1:1' ? 'feed11' : 'feed45';
}

/**
 * The texts of the Story (or the feed post of a shape) of one moment, with the context of step 3.
 * A feed post is the Story stack in its 4:5 or square frame. Pure.
 */
export function storyOfMoment(
  context: StoryPreviewContext,
  moment: StudioMoment | undefined,
  lyricLine?: string,
  assetTypeId?: StoryStackAssetType,
  shape?: KitShape
): KitStory {
  return studioStory({
    // Absent asset type: a Story.
    format: storyFormatOfStudio(assetTypeId ?? 'story', shape),
    presave: context.presave,
    // A lyric card is always the lyric moment.
    moment: assetTypeId === 'lyricCard' ? 'lyricLine' : moment,
    lyricLine,
    // A countdown preview shows the first day still to come (7, else 3, else 1).
    countdownDays: countdownDays(context.daysToRelease)[0] ?? 7,
    title: context.title,
    artist: context.artist,
    releaseDate: context.releaseDate,
    lyrics: context.lyrics,
    link: context.link,
    withLogo: context.logoUrl !== null,
    today: context.today,
    locale: context.locale,
  });
}
